import {
  breakpointSizes,
  getBreakpointDown,
  styleConstants,
} from '@paywithextend/components'
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import BudgetRequestsTable from 'src/components/BudgetRequestsTable/BudgetRequestsTable'
import FilterSection from 'src/components/FilterSection/FilterSection'
import NavAwayBox, { NavAwayProps } from 'src/components/NavAwayBox/NavAwayBox'
import TableEmptyState, {
  TableEmptyStateDesigns,
} from 'src/components/TableEmptyState/TableEmptyState'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { useDebounced, useWindowScrollBottomHandler } from 'src/hooks'
import { BudgetRequestListModel } from 'src/models/budgetRequest.models'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { css } from 'styled-components'

export interface BudgetRequestsBoxInfiniteScrollProps {
  fetch?: (page?: number) => void
  page?: number
  loading: boolean
  complete: boolean
  totalItems?: number
}

interface BudgetRequestsBoxWithFiltersProps {
  className?: string
  budgetRequests: BudgetRequestListModel[]
  isSlim: boolean
  defaultTitle: React.ReactElement
  navAway?: NavAwayProps
  infiniteScrollProps?: BudgetRequestsBoxInfiniteScrollProps
  readOnly?: boolean
  userInfiniteScrollProps?: UserDropdownInfiniteScrollProps
  totalBudgetRequests: number
  isApprover?: boolean
  clientHeight?: number
}

const BudgetRequestsBoxWithFilters: React.FC<
  BudgetRequestsBoxWithFiltersProps
> = (props): React.ReactElement => {
  const queryParams = useSelector(selectQueryParams)

  const [initialRender, setInitialRender] = useState<boolean>(true)
  const [pushNewSearchState, setPushNewSearchState] = useState<boolean>(false)
  const showNewBudgetsTable = useSelector(
    selectFeatureFlaggerFromState,
  )?.show_new_budgets_table

  const isDirty = false

  const handleScroll = useCallback((): void => {
    if (
      props.infiniteScrollProps !== undefined &&
      !props.infiniteScrollProps.loading &&
      !props.infiniteScrollProps.complete &&
      props.infiniteScrollProps.page !== undefined
    ) {
      props.infiniteScrollProps.fetch?.(props.infiniteScrollProps.page + 1)
    }
  }, [
    props.infiniteScrollProps?.loading,
    props.infiniteScrollProps?.complete,
    props.infiniteScrollProps?.fetch,
    props.infiniteScrollProps?.page,
  ])
  useWindowScrollBottomHandler(handleScroll)

  const { debouncedF } = useDebounced((): void => {
    props.infiniteScrollProps?.fetch?.(0)
  })

  useEffect((): void => {
    if (pushNewSearchState) {
      setPushNewSearchState(false)
    }
  }, [pushNewSearchState])

  useEffect((): void => {
    debouncedF()
  }, [])

  // TODO: This is removable, but was causing a strange bug. Will remove in a followup.
  useEffect((): void => {
    if (initialRender) {
      setInitialRender(false)
    }
  }, [queryParams])

  return (
    <NavAwayBox
      loading={props.infiniteScrollProps?.loading && !showNewBudgetsTable}
    >
      <FilterSection
        topLeftContent={
          <div
            css={css`
              width: 100%;
              display: flex;
              justify-content: space-between;
              column-gap: 40px;
              ${getBreakpointDown(breakpointSizes.XXSM)(css`
                flex-direction: column;
                row-gap: 20px;
              `)}
            `}
          >
            {props.defaultTitle}
          </div>
        }
      />
      <div
        css={css`
          background-color: ${styleConstants.colors.gray_lightest};
        `}
      >
        {props.budgetRequests.length > 0 && (
          <BudgetRequestsTable
            budgetRequests={props.budgetRequests}
            isSlim={props.isSlim}
            isApprover={props.isApprover}
            clientHeight={props.clientHeight}
          />
        )}
        {!isDirty &&
          props.budgetRequests.length === 0 &&
          props.infiniteScrollProps?.complete && (
            <TableEmptyState
              design={
                props.isApprover
                  ? TableEmptyStateDesigns.BudgetRequests
                  : TableEmptyStateDesigns.PendingBudgets
              }
              readOnly={!!props.readOnly}
            />
          )}
      </div>
    </NavAwayBox>
  )
}

export default BudgetRequestsBoxWithFilters
